import React, { JSX } from 'react';
import { Calendar, FileText } from 'lucide-react';
import { formatApiDateTime } from '../../../helpers/helpers';
import type { S3ObjectInfo } from '../../../../types/AWSTypes';
import { formatLogBytes, getLogFileName } from '../utils/logBrowseFileUtils';
import RefreshButton from '../../shared/RefreshButton';
import TableLoadingOverlay from '../../shared/TableLoadingOverlay';
import { PortaledLayer } from '../../shared/PortaledLayer';
import InlineSpinner from '../../shared/InlineSpinner';

import type { LogPreviewDialogProps } from '../types/LogViewerProps';

export type { LogPreviewDialogProps };

export default function LogPreviewDialog(props: LogPreviewDialogProps): JSX.Element | null {
    const {
        open,
        selectedKey,
        selectedObj,
        preview,
        busyPreview,
        busyDownload,
        onReloadPreview,
        onDownload,
        onClose,
        backdropZIndex = 50,
        dialogZIndex = 60,
    } = props;

    if (!open) return null;

    const file = selectedKey ? getLogFileName(selectedKey) : '';
    return (
        <PortaledLayer>
            <>
            <div
                className="fixed inset-0 bg-black/35 dark:bg-black/70"
                style={{ zIndex: backdropZIndex }}
                onClick={onClose}
            />
            <div
                className="fixed inset-0 flex items-center justify-center p-4"
                style={{ zIndex: dialogZIndex }}
                onClick={onClose}
            >
            <div
                className="flex max-h-[min(92vh,52rem)] w-full max-w-5xl min-h-0 flex-col overflow-hidden rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-lg"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-labelledby="log-preview-dialog-title"
            >
                <div className="border-b border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800 px-5 py-3">
                    <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                            <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-slate-400">
                                Log preview
                            </p>
                            <h2
                                id="log-preview-dialog-title"
                                className="mt-0.5 break-words text-lg font-semibold text-gray-900 dark:text-slate-100"
                            >
                                {file || 'Preview'}
                            </h2>
                            <p
                                className="mt-1 break-all font-mono text-xs text-gray-600 dark:text-slate-400"
                                title={selectedKey}
                            >
                                {selectedKey || 'No file selected'}
                            </p>
                        </div>
                        <button
                            type="button"
                            className="shrink-0 rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2 text-sm font-medium text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-800"
                            onClick={onClose}
                        >
                            Close
                        </button>
                    </div>
                </div>

                <div className="flex flex-wrap items-center gap-2 border-b border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-5 py-2.5">
                    <span className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-[11px] font-medium text-gray-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200">
                        <FileText className="h-3.5 w-3.5 text-gray-400 dark:text-slate-400" aria-hidden />
                        {formatLogBytes(selectedObj?.sizeBytes)}
                    </span>
                    <span className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-[11px] font-medium text-gray-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200">
                        <Calendar className="h-3.5 w-3.5 text-gray-400 dark:text-slate-400" aria-hidden />
                        {formatApiDateTime(selectedObj?.lastModifiedEpochMs)}
                    </span>
                    <span
                        className={[
                            'inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-medium',
                            busyPreview
                                ? 'border-gray-300 bg-gray-100 text-gray-800 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200'
                                : 'border-gray-200 bg-white text-gray-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200',
                        ].join(' ')}
                    >
                        {busyPreview ? 'Loading…' : 'Ready'}
                    </span>
                </div>

                <div className="flex flex-wrap items-center gap-2 border-b border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800 px-5 py-2.5">
                    <RefreshButton
                        busy={busyPreview}
                        onClick={onReloadPreview}
                        disabled={!selectedKey}
                        className="rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2 text-sm font-medium text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-800 disabled:opacity-50"
                        idleText="Reload"
                        busyText="Loading..."
                    />
                    <button
                        className="inline-flex items-center gap-2 rounded-md border border-blue-700 bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
                        onClick={onDownload}
                        disabled={busyDownload || !selectedKey}
                        type="button"
                    >
                        {busyDownload ? (
                            <>
                                <InlineSpinner size="xs" variant="current" />
                                Saving…
                            </>
                        ) : (
                            'Download'
                        )}
                    </button>
                </div>

                <TableLoadingOverlay loading={busyPreview} label="Loading preview..." size="sm" compact>
                    <div className="min-h-0 flex-1 overflow-auto overscroll-contain bg-white dark:bg-slate-900">
                        <pre className="whitespace-pre-wrap p-5 font-mono text-xs leading-relaxed text-gray-800 dark:text-slate-200">
                            {selectedKey ? (preview || 'No content') : 'Select a file'}
                        </pre>
                    </div>
                </TableLoadingOverlay>
            </div>
            </div>
            </>
        </PortaledLayer>
    );
}
